"use client";

import { Box, Columns, Row, Text } from "@dxp-feed/ui";
import { useState } from "@/utils/hooks/reactUseCustomHooks";
import NearByStores from "@/templates/storeDetail/NearByStores";
import DepartmentsToggle from "@/templates/storeDetail/DepartmentsToggle";
import { IListStores } from "@/utils/redux/storeLocator";
import { IDepartment } from "@/utils/types/storeLocator";

interface IWhatsNewInStoreProps {
  store: IListStores | null;
  selectedStore: IListStores;
  openDialog: () => void;
  setStoreToConfirm: (store: IListStores) => void;
}

const WhatsNewInStore: React.FC<IWhatsNewInStoreProps> = ({
  store,
  selectedStore,
  openDialog,
  setStoreToConfirm,
}) => {
  const [activeDepartment, setActiveDepartment] = useState<string | null>(null);

  const toggleDepartment = (departmentName: string, canToggle: boolean) => {
    if (canToggle) {
      setActiveDepartment((prevState) =>
        prevState === departmentName ? null : departmentName
      );
    }
  };

  const departmentsClassArray = [
    { iconClass: "icon-car", label: "Parking" },
    { iconClass: "icon-bread", label: "Bakery Department" },
    { iconClass: "icon-bread", label: "Bakery" },
    { iconClass: "icon-noodles", label: "Sushi and Noodle Bars" },
    { iconClass: "icon-deli-department", label: "Deli Department" },
    { iconClass: "icon-meat-slice", label: "Meat Department" },
    { iconClass: "icon-orange", label: "Produce Department" },
    { iconClass: "icon-floral", label: "Floral Department" },
    { iconClass: "icon-seafood", label: "Seafood Department" },
    { iconClass: "icon-jug", label: "Cafe" },
    { iconClass: "icon-beer", label: "Beer & Wine Sales" },
    { iconClass: "icon-beer", label: "Beer Department" },
    { iconClass: "icon-pill", label: "Pharmacy" },
    { iconClass: "icon-spa", label: "Natural Source Department" },
    { iconClass: "icon-liquor", label: "Bulk Foods Department" },
  ];

  const departments: IDepartment[] =
    (store?.departments as IDepartment[]) || [];
  //we are getting some deparment name as html fomart (eg Beer &amp; Wine Sales)
  //so convert this into normal string
  const decodeHTML = (input: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(input, "text/html");
    return doc.documentElement.textContent || "";
  };
  // Removiong duplicates based on the 'name' property (getting duplicate department from api)
  const seenNames = new Set();
  if (departments.length > 0) {
    for (let i = 0; i < departments.length; i++) {
      if (seenNames.has(departments[i].type)) {
        departments.splice(i, 1);
        i--;
      } else {
        seenNames.add(departments[i].type);
      }
    }
  }

  return (
    <>
      {departments &&
        departments.filter(
          (department) =>
            department.type && department.type.toLowerCase() !== "undefined"
        ).length > 0 && (
          <Row className="lgs:pb-12 pb-6">
            <Columns className="col-12">
              <Text as="h2" className="font-sans text-2xl pb-6 font-extrabold">
                What’s in the Store
              </Text>
              <p className="mb-6 text-body">
                All In-Store Services and Departments
              </p>
              <Box
                as="div"
                className="grid gap-6 lgs:grid-cols-3 md:grid-cols-2"
              >
                {departments
                  .filter(
                    (department) =>
                      department.type &&
                      department.type.toLowerCase() !== "undefined"
                  )
                  .map((department, index) => {
                    const departmentClass = departmentsClassArray.find(
                      (dClass) => dClass.label === decodeHTML(department.type)
                    );
                    const isToggled = activeDepartment === department.type;

                    return (
                      <Box
                        key={index}
                        className={`relative flex gap-4 items-center pb-2 border-grey350 border-b-[1px] cursor-pointer ${isToggled ? "after:absolute after:bottom-[-1px] after:h-[2px] after:w-full after:bg-brandSecondary" : ""}`}
                        onClick={() =>
                          toggleDepartment(
                            decodeHTML(department.type),
                            department.showDepartmentDetails
                          )
                        }
                      >
                        <Text
                          as="span"
                          className={`${departmentClass?.iconClass || ""} text-[#35A131]`}
                        />
                        <Text
                          as="p"
                          className="text-xl font-sans text-[#003D2A] font-extrabold"
                        >
                          {decodeHTML(department.type)}
                        </Text>
                        {department.showDepartmentDetails &&
                          departments.find(
                            (dept) =>
                              dept.type === decodeHTML(department.type) &&
                              (dept.showDepartmentDetails || isToggled)
                          ) && (
                            <Box
                              as="div"
                              className="absolute right-3 text-[12px]"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                setActiveDepartment(
                                  isToggled ? null : decodeHTML(department.type)
                                );
                              }}
                            >
                              {department.showDepartmentDetails &&
                              departments.find(
                                (dept) =>
                                  dept.type === decodeHTML(department.type) &&
                                  (dept.showDepartmentDetails || isToggled)
                              ) ? (
                                isToggled ? (
                                  <Text
                                    as="button"
                                    aria-label="close now"
                                    className="text-body font-bold underline text-brandSecondary"
                                  >
                                    Close
                                  </Text>
                                ) : (
                                  <Text
                                    as="button"
                                    aria-label="Get info"
                                    className="text-body font-bold underline text-brandSecondary"
                                  >
                                    Get info
                                  </Text>
                                )
                              ) : null}
                            </Box>
                          )}
                      </Box>
                    );
                  })}
              </Box>
              {/* Rendering dropdown for the active department */}
              {departments
                .filter(
                  (department) =>
                    department.type &&
                    department.type.toLowerCase() !== "undefined"
                )
                .map((department) => {
                  const isToggled =
                    activeDepartment === decodeHTML(department.type);
                  return (
                    isToggled &&
                    department.showDepartmentDetails && (
                      <DepartmentsToggle
                        key={decodeHTML(department.type)}
                        selectedDepartment={department}
                        closeDepartment={() => setActiveDepartment(null)}
                      />
                    )
                  );
                })}
            </Columns>
          </Row>
        )}

      {/* Near by stores component is always rendered */}
      <NearByStores
        store={store}
        selectedStore={selectedStore}
        openDialog={openDialog}
        setStoreToConfirm={setStoreToConfirm}
      />
    </>
  );
};

export default WhatsNewInStore;
 